import React, { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { v4 as uuidv4 } from 'uuid';

import { useAccounts } from '#hooks/useAccounts';
import { useNavigate } from '#hooks/useNavigate';
import { usePayees } from '#hooks/usePayees';

import { askAi } from './ai';
import { MtxPage, Section } from './ui';

type Reading = {
  amount: number | null;
  date: string | null;
  merchant: string | null;
  kind: 'expense' | 'bill';
};

const MAX_SIDE = 1600;

function drawToDataUrl(
  source: CanvasImageSource,
  width: number,
  height: number,
): string {
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.85);
}

/** A still image of the document: the photo itself, or a frame of the video. */
async function toImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    if (file.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.src = url;
      await new Promise<void>((resolve, reject) => {
        video.onloadeddata = () => resolve();
        video.onerror = () => reject(new Error('video'));
      });
      video.currentTime = Math.min(1, (video.duration || 2) / 2);
      await new Promise<void>(resolve => {
        video.onseeked = () => resolve();
      });
      return drawToDataUrl(video, video.videoWidth, video.videoHeight);
    }
    const image = new Image();
    image.src = url;
    await image.decode();
    return drawToDataUrl(image, image.naturalWidth, image.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function FacturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: accounts = [] } = useAccounts();
  const { data: payees = [] } = usePayees();
  const [status, setStatus] = useState<'idle' | 'reading' | 'failed' | 'saved'>(
    'idle',
  );
  const [reading, setReading] = useState<Reading | null>(null);
  const [accountId, setAccountId] = useState('');

  const open = accounts.filter(a => !a.closed);
  const account = accountId || open[0]?.id || '';

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setStatus('reading');
    setReading(null);
    try {
      const image = await toImage(file);
      const result = await askAi<Reading>('invoice', { image }, ['kind']);
      if (result) {
        setReading(result);
        setStatus('idle');
        return;
      }
    } catch {
      // Falls through to the failure message.
    }
    setStatus('failed');
  }

  async function confirm() {
    if (!reading || reading.amount === null || !account) {
      return;
    }
    const name = reading.merchant ?? t('Facture');
    const date = reading.date ?? monthUtils.currentDay();
    const amount = -Math.round(reading.amount * 100);
    const payee =
      payees.find(p => p.name.toLowerCase() === name.toLowerCase())?.id ??
      (await send('payee-create', { name }));

    if (reading.kind === 'bill') {
      await send('schedule/create', {
        schedule: { posts_transaction: false },
        conditions: [
          { op: 'is', field: 'date', value: date },
          { op: 'is', field: 'amount', value: amount },
          { op: 'is', field: 'account', value: account },
          { op: 'is', field: 'payee', value: payee },
        ],
      });
    } else {
      await send('transaction-add', {
        id: uuidv4(),
        account,
        date,
        amount,
        payee,
        notes: t('Lu sur une photo'),
      });
    }
    setStatus('saved');
  }

  const update = (patch: Partial<Reading>) =>
    setReading(current => (current ? { ...current, ...patch } : current));

  return (
    <MtxPage title={t('Lire une facture')}>
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Section style={{ padding: 16, gap: 10 }}>
          <Text style={{ color: theme.pageTextSubdued }}>
            {t(
              "Photographiez ou filmez un reçu ou une facture. Rien n'est enregistré avant votre confirmation.",
            )}
          </Text>
          <input
            aria-label={t('Photo ou vidéo de la facture')}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            onChange={event => void onFile(event)}
            style={{ minHeight: 44 }}
          />
          {status === 'reading' && (
            <Text>
              <Trans>Lecture en cours…</Trans>
            </Text>
          )}
          {status === 'failed' && (
            <Text style={{ color: theme.errorText }}>
              {t('Lecture impossible. Réessayez avec une photo plus nette.')}
            </Text>
          )}
        </Section>

        {reading && status !== 'saved' && (
          <Section title={t('Ce qui a été lu')} style={{ gap: 0 }}>
            <View style={{ padding: 12, gap: 8 }}>
              <Input
                aria-label={t('Commerçant ou créancier')}
                placeholder={t('Commerçant ou créancier')}
                value={reading.merchant ?? ''}
                onChangeValue={merchant => update({ merchant })}
                style={{ minHeight: 44 }}
              />
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Input
                  aria-label={t('Montant')}
                  placeholder={t('Montant')}
                  inputMode="decimal"
                  value={reading.amount === null ? '' : String(reading.amount)}
                  onChangeValue={value => {
                    const amount = Number(value.replace(',', '.'));
                    update({
                      amount:
                        Number.isFinite(amount) && value !== '' ? amount : null,
                    });
                  }}
                  style={{ flex: 1, minWidth: 0, minHeight: 44 }}
                />
                <Input
                  aria-label={t('Date')}
                  type="date"
                  value={reading.date ?? ''}
                  onChangeValue={date => update({ date: date || null })}
                  style={{ flex: 1, minWidth: 0, minHeight: 44 }}
                />
              </View>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Button
                  variant={reading.kind === 'expense' ? 'primary' : 'normal'}
                  onPress={() => update({ kind: 'expense' })}
                  style={{ flex: 1, minWidth: 0, minHeight: 44 }}
                >
                  {t('Dépense déjà payée')}
                </Button>
                <Button
                  variant={reading.kind === 'bill' ? 'primary' : 'normal'}
                  onPress={() => update({ kind: 'bill' })}
                  style={{ flex: 1, minWidth: 0, minHeight: 44 }}
                >
                  {t('Facture à payer')}
                </Button>
              </View>
              <label style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                <Trans>Compte</Trans>
                <select
                  value={account}
                  onChange={event => setAccountId(event.target.value)}
                  style={{
                    display: 'block',
                    width: '100%',
                    minHeight: 44,
                    marginTop: 4,
                  }}
                >
                  {open.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                variant="primary"
                isDisabled={reading.amount === null || !account}
                onPress={() => void confirm()}
                style={{ minHeight: 48, fontWeight: 600 }}
              >
                {reading.kind === 'bill'
                  ? t("Créer l'échéance")
                  : t('Créer la dépense')}
              </Button>
            </View>
          </Section>
        )}

        {status === 'saved' && (
          <Section style={{ padding: 16, gap: 10 }}>
            <Text style={{ color: theme.noticeTextLight, fontWeight: 600 }}>
              {t('Enregistré.')}
            </Text>
            <Button
              onPress={() => navigate('/quinzaine')}
              style={{ minHeight: 44 }}
            >
              {t('Retour à Ma quinzaine')}
            </Button>
          </Section>
        )}
      </View>
    </MtxPage>
  );
}
